import { ChartInfoKind } from '@frontend/chart/types';
import { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { LuInfo } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { CHART_CARD_INFO_MESSAGES, CHART_CARD_INFO_SECTION_HEADINGS } from './chart-card-info-messages';

const TOOLTIP_CLASS_NAME =
    'border-border-default bg-background-surface z-1000 max-w-sm rounded-md border shadow-sm [&_.tooltip-inner]:bg-background-surface [&_.tooltip-inner]:text-foreground-default [&_.tooltip-inner]:max-w-sm [&_.tooltip-inner]:p-0 [&_.tooltip-inner]:text-start';

/** Keeps chart-card info tooltips inside the viewport (Insights overflow / edge cards). */
export const CHART_CARD_INFO_TOOLTIP_POPPER_CONFIG = {
    strategy: 'fixed' as const,
    modifiers: [
        {
            name: 'flip',
            options: {
                fallbackPlacements: ['bottom', 'right', 'left', 'top']
            }
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: 'viewport',
                padding: 8
            }
        }
    ]
};

export interface ChartCardInfoIconProps {
    infoKind: ChartInfoKind;
    compact?: boolean;
}

export const ChartCardInfoIcon = ({ infoKind, compact = false }: ChartCardInfoIconProps) => {
    const intl = useIntl();
    const tooltipId = useId().replace(/:/g, '');
    const messages = CHART_CARD_INFO_MESSAGES[infoKind];
    const iconSize = compact ? 'size-3.5' : 'size-4';

    return (
        <OverlayTrigger
            placement='top'
            flip
            delay={{ show: 300, hide: 150 }}
            popperConfig={CHART_CARD_INFO_TOOLTIP_POPPER_CONFIG}
            overlay={
                <Tooltip
                    id={tooltipId}
                    className={TOOLTIP_CLASS_NAME}>
                    <div className='flex flex-col gap-3 p-3 text-sm'>
                        <div>
                            <div className='text-foreground-default mb-1 font-semibold'>
                                <FormattedMessage {...CHART_CARD_INFO_SECTION_HEADINGS.dataDescription} />
                            </div>
                            <div className='text-foreground-muted'>
                                <FormattedMessage {...messages.dataDescription} />
                            </div>
                        </div>
                        <div>
                            <div className='text-foreground-default mb-1 font-semibold'>
                                <FormattedMessage {...CHART_CARD_INFO_SECTION_HEADINGS.conclusions} />
                            </div>
                            <div className='text-foreground-muted'>
                                <FormattedMessage {...messages.conclusions} />
                            </div>
                        </div>
                    </div>
                </Tooltip>
            }>
            <span
                className='text-foreground-muted hover:text-foreground-default inline-flex shrink-0 cursor-help'
                role='img'
                aria-label={intl.formatMessage({
                    id: 'chart.components.chart-card-info.chart-card-info-icon.aria-label',
                    description: 'Accessible label for the icon that opens chart information',
                    defaultMessage: 'Chart information'
                })}
                onClick={(event) => event.stopPropagation()}>
                <LuInfo className={iconSize} />
            </span>
        </OverlayTrigger>
    );
};
